import { useMemo, useState } from 'react';

import {
  useGetMappingReleasesQuery,
  useGetMappingRequestsQuery,
  useGetMappingStockQuery,
  REQUEST_TYPES
} from '@/entities/dashboard/mapping';

import { useMapLocations } from '@/features/map-locations';
import { useMapCorridors } from '@/features/map-corridors';
import { useMapLayers } from '@/features/map-layers';
import { buildClusters, createClusterNameResolver, useMapGrouping } from '@/features/map-grouping';
import { useTimeMachine } from '@/features/map-time-machine';

import { useProjectSiteParams } from '@/shared/lib';

import { buildClusterSources, weeksAvailable, type ReportFeatures } from './report-values';

interface UseMappingDashboardProps {
  organizationId?: string;
  /** Vehicle category ids — the API only applies them to corridors (internal transfers). */
  vehicleCategories?: string[];
  /** Partial consignment (CMR) number. Pass the debounced value — it hits the API directly. */
  consignmentNumber?: string;
}

export function useMappingDashboard({
  organizationId,
  vehicleCategories,
  consignmentNumber
}: UseMappingDashboardProps = {}) {
  const { projectId, siteId } = useProjectSiteParams();
  const [showTimeMachine, setShowTimeMachine] = useState(false);

  const layers = useMapLayers();
  const { groupBy, setGroupBy } = useMapGrouping();

  const { data: locationsData, locations, isLoading: locationsLoading, error } = useMapLocations();

  /**
   * No `weekNo`: the whole weekly series arrives with the first response, so the time
   * machine reads it locally instead of refetching four datasets per slider step.
   *
   * The `|| undefined` is load-bearing, not noise. Both filters are URL state, so an
   * unset one is an empty string, and RTK Query only drops `undefined` from query
   * params (`stripUndefined`) — an empty string would travel as `organizationId=`,
   * which the API declares as an `Integer`.
   */
  const queryParams = useMemo(
    () => ({
      projectId,
      siteId,
      organizationId: organizationId || undefined,
      consignmentNumber: consignmentNumber || undefined
    }),
    [projectId, siteId, organizationId, consignmentNumber]
  );

  const { data: stockData, isFetching: isStockFetching } = useGetMappingStockQuery(queryParams, {
    skip: !layers.reports.includes('SOH')
  });

  const { data: releasesData, isFetching: isReleasesFetching } = useGetMappingReleasesQuery(queryParams, {
    skip: !layers.reports.includes('REL')
  });

  const { data: deliveriesData, isFetching: isDeliveriesFetching } = useGetMappingRequestsQuery(
    { type: REQUEST_TYPES.CONSIGNMENTS, ...queryParams },
    { skip: !layers.reports.includes('RIC') }
  );

  const { data: pickupsData, isFetching: isPickupsFetching } = useGetMappingRequestsQuery(
    { type: REQUEST_TYPES.PICKUP, ...queryParams },
    { skip: !layers.reports.includes('RPU') }
  );

  const features: ReportFeatures = useMemo(
    () => ({
      SOH: stockData?.features,
      REL: releasesData?.features,
      RIC: deliveriesData?.features,
      RPU: pickupsData?.features
    }),
    [stockData, releasesData, deliveriesData, pickupsData]
  );

  const timeMachine = useTimeMachine({ weeksAvailable: useMemo(() => weeksAvailable(features), [features]) });
  const { weeksAgo } = timeMachine;

  const clusters = useMemo(() => {
    const sources = buildClusterSources(locations, features, layers.reports, weeksAgo);

    return buildClusters(sources, groupBy, createClusterNameResolver(locationsData, groupBy));
  }, [locations, features, layers.reports, weeksAgo, groupBy, locationsData]);

  const iconLocations = useMemo(
    () => locations.filter((location) => layers.locationTypes.includes(location.type)),
    [locations, layers.locationTypes]
  );

  const corridors = useMapCorridors({
    locations,
    types: layers.corridorTypes,
    organizationId,
    vehicleCategories,
    consignmentNumber,
    weeksAgo
  });

  // Stable reference on purpose: the marker layer is memoised on this object, so a
  // fresh literal per render would defeat the memo (353351). Empty filters become
  // `undefined` for the reason given above — the popups query the API with these too.
  const dataFilters = useMemo(
    () => ({ organizationId: organizationId || undefined, consignmentNumber: consignmentNumber || undefined }),
    [organizationId, consignmentNumber]
  );

  const isDataFetching = isStockFetching || isReleasesFetching || isDeliveriesFetching || isPickupsFetching;

  return {
    locations,
    clusters,
    iconLocations,
    corridors,
    dataFilters,

    layers,
    groupBy,
    setGroupBy,
    timeMachine,
    weeksAgo,
    showTimeMachine,
    setShowTimeMachine,

    isLoading: locationsLoading || isDataFetching,
    isDataFetching,
    error
  };
}
